import { useState, useMemo, useCallback, useEffect } from "react";
import {
  formatCountWithArabicNumbers,
  formatPercentageWithArabicNumbers,
} from "@/utils/arabicNumbers";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Calendar as CalendarIcon,
  User,
  FileText,
  FileDown,
  Loader2,
  Clock,
  TrendingUp,
} from "lucide-react";
import { ArabicCalendar } from "@/components/ui/arabic-calendar";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { generateStudentLatePDF } from "@/utils/studentLatePdfGenerator";
import { AttendanceReportFilters } from "../filters/AttendanceReportFilters";
import { supabase } from "@/integrations/supabase/client";
import { dateSplitter } from "@/utils/formatting/deteSplitter";

// دالة للحصول على اسم اليوم بالعربية
function getArabicDayName(date: Date): string {
  const days = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];
  return days[date.getDay()];
}

function formatHijriDate(date: Date) {
  try {
    // استخدام Intl.DateTimeFormat للحصول على تاريخ هجري دقيق
    const hijriFormatter = new Intl.DateTimeFormat(
      "ar-SA-u-ca-islamic-umalqura",
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    );

    const parts = hijriFormatter.formatToParts(date);
    const day = parts.find((part) => part.type === "day")?.value || "01";
    const month = parts.find((part) => part.type === "month")?.value || "01";
    const year = parts.find((part) => part.type === "year")?.value || "1446";

    // تحويل الأرقام إلى أرقام هندية
    const toArabicIndic = (num: string): string => {
      const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      return num.replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)]);
    };

    // ترتيب: سنة/شهر/يوم (من اليمين لليسار)
    return `${toArabicIndic(year)}/${toArabicIndic(month)}/${toArabicIndic(
      day
    )} هـ`;
  } catch (error) {
    console.error("خطأ في تحويل التاريخ الهجري:", error);
    // بديل: استخدام التاريخ الميلادي مع أرقام هندية
    const fallback = date
      .toLocaleDateString("ar-SA")
      .replace(/\d/g, (digit) => {
        const arabicNumerals = [
          "٠",
          "١",
          "٢",
          "٣",
          "٤",
          "٥",
          "٦",
          "٧",
          "٨",
          "٩",
        ];
        return arabicNumerals[parseInt(digit)];
      });
    return fallback;
  }
}

interface LateReportTabProps {
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  selectedStage: string;
  handleStageChange: (value: string) => void;
  selectedGrade: string;
  handleGradeChange: (value: string) => void;
  selectedSection: string;
  setSelectedSection: (value: string) => void;
  students: any[];
  lateRecords?: any[];
  validEnabledStages: any[];
  filteredAvailableGrades: string[];
  availableSectionsForGrade: string[];
  safeGetStageDisplayName: (stage: any) => string;
  // selectedStudent: string;
  // setSelectedStudent: (value: string) => void;
}

export const LateReportTab = ({
  students,
  // lateRecords,
  validEnabledStages,
  safeGetStageDisplayName,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedStage,
  handleStageChange,
  selectedGrade,
  handleGradeChange,
  selectedSection,
  setSelectedSection,
  // selectedStudent,
  // setSelectedStudent,
  filteredAvailableGrades,
  availableSectionsForGrade,
}: LateReportTabProps) => {
  // States for filters
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  // const [selectedStage, setSelectedStage] = useState<string>("");
  // const [selectedGrade, setSelectedGrade] = useState<string>("");
  // const [selectedSection, setSelectedSection] = useState<string>("");
  const [lateRecords, setLates] = useState<any[]>([]);
  // const [startDate, setStartDate] = useState<Date>();
  // const [endDate, setEndDate] = useState<Date>();
  const [isDownloading, setIsDownloading] = useState(false);
  // console.log("lateRecords: ", lateRecords);

  // Get available options based on selections
  // const availableGrades = useMemo(() => {
  //   if (selectedStage) {
  //     const stage = validEnabledStages.find((s) => s.id === selectedStage);
  //     return stage?.grades?.map((g: any) => g.name) || [];
  //   }
  //   return [...new Set(students.map((s) => s.grade))];
  // }, [selectedStage, validEnabledStages, students]);

  // const availableSections = useMemo(() => {
  //   if (selectedStage && selectedGrade) {
  //     const stage = validEnabledStages.find((s) => s.id === selectedStage);
  //     const grade = stage?.grades?.find((g: any) => g.name === selectedGrade);
  //     return grade?.sections || [];
  //   }
  //   if (selectedGrade) {
  //     return [
  //       ...new Set(
  //         students
  //           .filter((s) => s.grade === selectedGrade)
  //           .map((s) => s.section)
  //       ),
  //     ];
  //   }
  //   return [...new Set(students.map((s) => s.section))];
  // }, [selectedStage, selectedGrade, validEnabledStages, students]);

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      if (selectedStage) {
        const stage = validEnabledStages.find((s) => s.id === selectedStage);
        if (stage && stage.grades) {
          const stageGrades = stage.grades.map((g: any) => g.name);
          if (!stageGrades.includes(student.grade)) return false;
        }
      }
      if (selectedGrade && student.grade !== selectedGrade) return false;
      if (selectedSection && student.section !== selectedSection) return false;
      return true;
    });
  }, [
    students,
    selectedStage,
    selectedGrade,
    selectedSection,
    validEnabledStages,
  ]);

  // Calculate student late data
  const studentData = useMemo(() => {
    if (!selectedStudent) return null;

    const student = students.find((s) => s.id === selectedStudent);
    if (!student) return null;

    // Filter late records for this student and date range
    const studentLateRecords = lateRecords.filter((record) => {
      if (record.student_id !== selectedStudent) return false;
      if (startDate || endDate) {
        const recordDate = new Date(record.date);
        if (startDate && recordDate < startDate) return false;
        if (endDate && recordDate > endDate) return false;
      }
      return true;
    });

    // Calculate total days in the selected period
    let totalDays = 0;
    if (startDate && endDate) {
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } else {
      totalDays = 30; // Default period
    }

    const lateCount = studentLateRecords.length;
    const latePercentage =
      totalDays > 0 ? Math.round((lateCount / totalDays) * 100) : 0;

    return {
      student,
      totalDays,
      lateCount,
      latePercentage,
      lateDays: studentLateRecords,
    };
  }, [selectedStudent, students, lateRecords, startDate, endDate]);

  const fetchLates = useCallback(async () => {
    if (!selectedStudent) {
      setLates([]);
      return;
    }

    let query = supabase
      .from("late_attendance_records")
      .select("*")
      .eq("student_id", selectedStudent);

    if (startDate) {
      query = query.gte("date", dateSplitter(startDate));
    }
    if (endDate) {
      query = query.lte("date", dateSplitter(endDate));
    }

    const { data, error } = await query;
    if (error) {
      // handle error (toast, etc.)
      setLates([]);
    } else {
      setLates(data || []);
    }
    // setLoadingProfiles(false);
  }, [endDate, selectedStudent, startDate]);

  useEffect(() => {
    if (selectedStudent) fetchLates();
  }, [fetchLates, selectedStudent]);

  // const handleStageChange = (value: string) => {
  //   setSelectedStage(value);
  //   setSelectedGrade("");
  //   setSelectedSection("");
  //   setSelectedStudent("");
  // };

  // const handleGradeChange = (value: string) => {
  //   setSelectedGrade(value);
  //   setSelectedSection("");
  //   setSelectedStudent("");
  // };

  // const handleSectionChange = (value: string) => {
  //   setSelectedSection(value);
  //   setSelectedStudent("");
  // };

  // Handle PDF download for student late report
  const handleDownloadLatePDF = async () => {
    if (!selectedStudent || !startDate) {
      toast({
        title: "بيانات مطلوبة",
        description: "يجب اختيار الطالب وتحديد تاريخ البداية لإنشاء التقرير",
        variant: "destructive",
      });
      return;
    }

    if (!studentData) {
      toast({
        title: "لا توجد بيانات",
        description: "لا توجد بيانات للطالب المحدد في الفترة المختارة",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);

    try {
      toast({
        title: "بدء التحميل",
        description: "جاري إنشاء تقرير تأخر الطالب PDF...",
      });

      // Create student late report data
      const reportData = {
        student: studentData.student,
        period: endDate
          ? `من ${formatHijriDate(startDate)} إلى ${formatHijriDate(endDate)}`
          : `من ${formatHijriDate(startDate)}`,
        totalDays: studentData.totalDays,
        lateCount: studentData.lateCount,
        latePercentage: studentData.latePercentage,
        lateDays: studentData.lateDays.map((record) => ({
          date: format(new Date(record.date), "yyyy/MM/dd"),
          day: getArabicDayName(new Date(record.date)),
          hijriDate: formatHijriDate(new Date(record.date)),
          notes: record.notes || "",
        })),
      };

      // Generate PDF using the professional generator
      await generateStudentLatePDF(reportData);

      toast({
        title: "تم التنزيل بنجاح",
        description: "تم تنزيل تقرير تأخر الطالب بصيغة PDF",
      });
    } catch (error) {
      console.error("خطأ في تنزيل تقرير التأخر:", error);
      toast({
        title: "خطأ في التنزيل",
        description: "حدث خطأ أثناء تنزيل تقرير التأخر",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Download Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={handleDownloadLatePDF}
          className="bg-primary-blue hover:bg-primary-blue/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          size="sm"
          disabled={!studentData || isDownloading || !startDate}
        >
          {isDownloading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin text-orange-600" />
              <span className="text-orange-600">جاري التحميل...</span>
            </>
          ) : (
            <>
              <FileDown className="h-4 w-4 mr-2" />
              تنزيل تقرير PDF
            </>
          )}
        </Button>
      </div>
      {/* Filters Card */}
      {/* <Card>
        <div className="bg-secondary text-white rounded-t-lg p-4">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-white" />
            <h3 className="text-lg font-semibold text-white">
              تقرير تأخر طالب محدد
            </h3>
          </div>
        </div>
        <CardContent className="space-y-4 pt-6"> */}
      <AttendanceReportFilters
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedStage={selectedStage}
        handleStageChange={handleStageChange}
        selectedGrade={selectedGrade}
        handleGradeChange={handleGradeChange}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        validEnabledStages={validEnabledStages}
        safeGetStageDisplayName={safeGetStageDisplayName}
        filteredAvailableGrades={filteredAvailableGrades}
        availableSectionsForGrade={availableSectionsForGrade}
        selectedStudent={selectedStudent}
        setSelectedStudent={setSelectedStudent}
        enableStudentSelect
        filteredStudents={filteredStudents}
      />
      {selectedStudent && !startDate && (
        <Card>
          <CardContent className="text-center py-6">
            <div className="text-muted-foreground">
              الخطوة التالية: حدد تاريخ البداية لعرض تقرير التأخر
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date Range Selection */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-primary-blue">
                تاريخ البداية <span className="text-red-500">*</span>
              </label>
              <ArabicCalendar
                value={startDate}
                onChange={setStartDate}
                placeholder="اختر تاريخ البداية"
                className={`bg-white/70 ${
                  !startDate ? "border-red-300" : "border-blue-200"
                }`}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-primary-blue">
                تاريخ النهاية
              </label>
              <ArabicCalendar
                value={endDate}
                onChange={setEndDate}
                placeholder="اختر تاريخ النهاية"
                className={`bg-white/70 ${
                  !endDate ? "border-red-300" : "border-blue-200"
                }`}
              />
            </div>
          </div> */}
      {/* </CardContent>
      </Card> */}

      {/* Student Late Report */}
      {studentData && startDate && (
        <div className="space-y-6">
          {/* Late Attendance Report */}
          <Card>
            <div className="bg-secondary text-white rounded-t-lg p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-white" />
                <h3 className="text-lg font-semibold text-white">
                  تقرير التأخر
                </h3>
              </div>
            </div>
            <CardContent className="pt-6">
              {/* Statistics */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-blue/10 to-transparent rounded-xl border-l-4 border-brand-blue border border-brand-blue/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-blue/10 rounded-full flex items-center justify-center">
                        <CalendarIcon className="h-4 w-4 text-brand-blue" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-blue">
                      أيام الفترة
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-blue">
                      {formatCountWithArabicNumbers(studentData.totalDays)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-teal/10 to-transparent rounded-xl border-l-4 border-brand-teal border border-brand-teal/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-teal/10 rounded-full flex items-center justify-center">
                        <Clock className="h-4 w-4 text-brand-teal" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-teal">
                      مرات التأخر
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-teal">
                      {formatCountWithArabicNumbers(studentData.lateCount)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-transparent via-brand-orange/10 to-transparent rounded-xl border-l-4 border-brand-orange border border-brand-orange/20 shadow-sm hover:shadow-md transition-shadow backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-brand-orange/10 rounded-full flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-brand-orange" />
                      </div>
                    </div>
                    <div className="text-xs font-medium text-brand-orange">
                      نسبة التأخر
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold text-brand-orange">
                      {formatPercentageWithArabicNumbers(
                        studentData.latePercentage
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Late Table */}
              {studentData.lateDays.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">تفاصيل أيام التأخر</h4>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-teal-500 text-white">
                          <th className="px-6 py-4 text-right font-medium">
                            م
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            اليوم
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            التاريخ
                          </th>
                          <th className="px-6 py-4 text-right font-medium">
                            الحالة
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {studentData.lateDays.map((record, index) => (
                          <tr
                            key={record.id}
                            className="border-b border-gray-100 hover:bg-gray-50/50"
                          >
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-medium text-sm">
                                {index + 1}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {getArabicDayName(new Date(record.date))}
                            </td>
                            <td className="px-6 py-4 font-medium">
                              {formatHijriDate(new Date(record.date))}
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                متأخر
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {studentData.lateDays.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="text-lg font-semibold mb-2">
                    لا توجد أيام تأخر في الفترة المحددة
                  </div>
                  <div className="text-sm font-normal text-muted-foreground/70">
                    الطالب لم يتأخر في هذه الفترة
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Data Message */}
      {!studentData && selectedStudent && startDate && (
        <Card>
          <CardContent className="text-center py-8">
            <div className="text-muted-foreground">
              لا توجد سجلات تأخر للطالب في الفترة المحددة
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
